import format from 'date-fns/format';
import { useState } from 'react';
import { DateRange } from 'react-day-picker';

import { Button } from './Button';
import { Calendar } from './Calendar';
import { Icon } from './Icon';
import { Stack } from './Stack';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { cn } from './utils/cn';

interface DateRangeSelectorProps {
  dateRange?: DateRange;
  onDateRangeChange: (dateRange?: DateRange) => void;
  align?: 'start' | 'center' | 'end';
  className?: string;
  disabled?: boolean;
  /**
   * Default text displayed when no date range is selected.
   * Defaults to "Select date range".
   */
  placeholder?: string;
  /**
   * Custom render function for the trigger button.
   * Receives the formatted date range text and should return a React element.
   * When provided, this will be used instead of the default button.
   */
  renderTrigger?: (formattedDateRange: React.ReactNode) => React.ReactElement;
}

export const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  dateRange,
  onDateRangeChange,
  align = 'center',
  className,
  disabled = false,
  placeholder = 'Select date range',
  renderTrigger,
}) => {
  const [open, setOpen] = useState(false);
  const disableClosing = Boolean(dateRange?.from && !dateRange?.to);

  const handleDateRangeChange = (range?: DateRange, selectedDay?: Date) => {
    // If both dates are already set and user clicks a new date, reset to start a new range
    if (dateRange?.from && dateRange?.to && range?.from) {
      onDateRangeChange({ from: selectedDay, to: undefined });
      return;
    }

    onDateRangeChange(range);
  };

  const handleOpenChange = (newOpen: boolean) => {
    // Prevent closing if we're in the middle of selecting a range
    if (!newOpen && disableClosing) {
      return;
    }
    setOpen(newOpen);
  };

  const formatDateRange = () => {
    if (!dateRange?.from) {
      return <span>{placeholder}</span>;
    }

    if (!dateRange.to) {
      return format(dateRange.from, 'PP');
    }

    return `${format(dateRange.from, 'PP')} - ${format(dateRange.to, 'PP')}`;
  };

  const formattedDateRange = formatDateRange();

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {renderTrigger ? (
          renderTrigger(formattedDateRange)
        ) : (
          <button
            className={cn(
              'rounded-sm border border-bg-quaternary bg-transparent p-2 text-text-secondary outline-hidden hover:border-border-secondary focus:border-border-accent-quaternary',
              'flex min-w-[280px] cursor-pointer items-center justify-between gap-2',
              disabled && 'pointer-events-none',
              className,
            )}
          >
            <Icon name="Calendar" />
            {formattedDateRange}
            <Icon name="ChevronDown" className="ml-auto" />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align={align}>
        <Calendar
          mode="range"
          selected={dateRange}
          defaultMonth={dateRange?.from}
          onSelect={handleDateRangeChange}
          numberOfMonths={2}
          showOutsideDays={false}
          autoFocus
          footer={
            <Stack direction="horizontal" justify="end" align="stretch">
              <Button onClick={() => setOpen(false)} disabled={disableClosing}>
                Done
              </Button>
            </Stack>
          }
        />
      </PopoverContent>
    </Popover>
  );
};
